// src/components/maps/admin/POIEditor.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, ImageOverlay, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import POIMarker from '../POIMarker';
import indoorMappingService from '../../../services/indoorMappingService';

// Default center: Vinktstraat 1, Deinze, Belgium
const DEFAULT_CENTER = [50.9847, 3.5278];

/**
 * Map click handler for placing POIs
 */
const POIPlacementHandler = ({ enabled, onPlace }) => {
  useMapEvents({
    click: (e) => {
      if (enabled) {
        onPlace(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
};

/**
 * POI Editor component for placing and managing points of interest
 */
const POIEditor = ({ floorPlan }) => {
  const [pois, setPois] = useState([]);
  const [poiTypes, setPoiTypes] = useState([]);
  const [placementMode, setPlacementMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPoi, setEditingPoi] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Form state - using API field names
  const [poiForm, setPoiForm] = useState({
    name: '',
    poi_type_id: '',
    description: '',
    geo_lat: 0,
    geo_lng: 0,
  });

  // Load POI types on mount
  useEffect(() => {
    loadPOITypes();
  }, []);

  // Load existing POIs when floor plan changes
  useEffect(() => {
    if (floorPlan?.id) {
      loadPOIs();
    }
  }, [floorPlan]);

  const loadPOITypes = async () => {
    try {
      const types = await indoorMappingService.getPOITypes();
      setPoiTypes(types);
      // Set default type if available
      if (types.length > 0 && !poiForm.poi_type_id) {
        setPoiForm(prev => ({ ...prev, poi_type_id: types[0].id }));
      }
    } catch (err) {
      console.error('Failed to load POI types:', err);
      // Use fallback types if API call fails
      setPoiTypes([
        { id: 1, name: 'Office' },
        { id: 2, name: 'Restaurant' },
        { id: 3, name: 'Restroom' },
        { id: 4, name: 'Exit' },
        { id: 5, name: 'Stairs' },
        { id: 6, name: 'Elevator' },
        { id: 7, name: 'Entrance' },
        { id: 8, name: 'Medical' },
      ]);
    }
  };

  const loadPOIs = async () => {
    try {
      const data = await indoorMappingService.getPOIs(floorPlan.id);
      setPois(data);
    } catch (err) {
      console.error('Failed to load POIs:', err);
      showSnackbar('Failed to load POIs', 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handlePlacePOI = (lat, lng) => {
    setPoiForm({
      ...poiForm,
      geo_lat: lat,
      geo_lng: lng,
    });
    setDialogOpen(true);
    setPlacementMode(false);
  };

  const resetForm = () => {
    setPoiForm({
      name: '',
      poi_type_id: poiTypes.length > 0 ? poiTypes[0].id : '',
      description: '',
      geo_lat: 0,
      geo_lng: 0,
    });
  };

  const handleSavePOI = async () => {
    try {
      // Convert geo coordinates to pixel coordinates
      const { x, y } = geoToPixel(poiForm.geo_lat, poiForm.geo_lng);

      // Build data with both geo and pixel coordinates
      const dataWithPixels = {
        ...poiForm,
        x_position: x,
        y_position: y,
      };

      if (editingPoi) {
        // Update existing POI
        const updated = await indoorMappingService.updatePOI(editingPoi.id, dataWithPixels);
        setPois(pois.map((p) => (p.id === editingPoi.id ? updated : p)));
        showSnackbar('POI updated successfully', 'success');
      } else {
        // Create new POI
        const newPoi = await indoorMappingService.createPOI(floorPlan.id, dataWithPixels);
        setPois([...pois, newPoi]);
        showSnackbar('POI created successfully', 'success');
      }

      setDialogOpen(false);
      setEditingPoi(null);
      resetForm();
    } catch (err) {
      showSnackbar(err.message || 'Failed to save POI', 'error');
    }
  };

  const handleEditPOI = (poi) => {
    setEditingPoi(poi);
    setPoiForm({
      name: poi.name,
      poi_type_id: poi.poi_type_id || poi.poiType?.id || '',
      description: poi.description || '',
      geo_lat: poi.geo_lat ?? poi.y_position ?? 0,
      geo_lng: poi.geo_lng ?? poi.x_position ?? 0,
    });
    setDialogOpen(true);
  };

  const handleDeletePOI = async (poiId) => {
    if (!window.confirm('Are you sure you want to delete this POI?')) {
      return;
    }

    try {
      await indoorMappingService.deletePOI(poiId);
      setPois(pois.filter((p) => p.id !== poiId));
      showSnackbar('POI deleted successfully', 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to delete POI', 'error');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPoi(null);
    resetForm();
  };

  // Handle both snake_case (API) and camelCase field names for image URL
  const imageUrl = floorPlan?.image_url || floorPlan?.imageUrl;
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
  const fullImageUrl = imageUrl
    ? (imageUrl.startsWith('http') ? imageUrl : `${API_BASE_URL}${imageUrl}`)
    : null;

  // Calculate bounds - handle both nested object and flat properties
  const getBounds = () => {
    // Check for nested bounds object
    if (floorPlan?.bounds?.north && floorPlan?.bounds?.south && floorPlan?.bounds?.east && floorPlan?.bounds?.west) {
      return {
        north: floorPlan.bounds.north,
        south: floorPlan.bounds.south,
        east: floorPlan.bounds.east,
        west: floorPlan.bounds.west,
      };
    }
    // Check for flat properties (snake_case from API)
    if (floorPlan?.bounds_north && floorPlan?.bounds_south && floorPlan?.bounds_east && floorPlan?.bounds_west) {
      return {
        north: parseFloat(floorPlan.bounds_north),
        south: parseFloat(floorPlan.bounds_south),
        east: parseFloat(floorPlan.bounds_east),
        west: parseFloat(floorPlan.bounds_west),
      };
    }
    return null;
  };

  const boundsData = getBounds();
  const bounds = boundsData
    ? [
        [boundsData.south, boundsData.west],
        [boundsData.north, boundsData.east],
      ]
    : null;

  const mapCenter = bounds
    ? [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2]
    : DEFAULT_CENTER;

  /**
   * Convert geographic coordinates to pixel coordinates on the floor plan image
   * @param {number} lat - Geographic latitude
   * @param {number} lng - Geographic longitude
   * @returns {object} { x, y } pixel coordinates
   */
  const geoToPixel = useCallback((lat, lng) => {
    if (!boundsData || !floorPlan?.image_width || !floorPlan?.image_height) {
      console.warn('[POIEditor] Cannot convert geo to pixel - missing bounds or image dimensions');
      return { x: 0, y: 0 };
    }

    const { north, south, east, west } = boundsData;

    // Calculate relative position within bounds (0-1)
    const xRatio = (lng - west) / (east - west);
    const yRatio = (north - lat) / (north - south); // Inverted because y increases downward in images

    // Convert to pixel coordinates
    const x = Math.round(xRatio * floorPlan.image_width);
    const y = Math.round(yRatio * floorPlan.image_height);

    return { x, y };
  }, [boundsData, floorPlan?.image_width, floorPlan?.image_height]);

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          POI Editor
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Place and manage points of interest on the floor plan.
        </Typography>

        <Button
          variant={placementMode ? 'contained' : 'outlined'}
          onClick={() => setPlacementMode(!placementMode)}
          sx={{ mb: 2 }}
        >
          {placementMode ? 'Cancel Placement' : 'Place New POI'}
        </Button>

        {placementMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Click on the map to place a new POI
          </Alert>
        )}

        {/* POI List */}
        <Typography variant="subtitle2" gutterBottom>
          Existing POIs ({pois.length})
        </Typography>
        <List dense>
          {pois.map((poi) => (
            <ListItem
              key={poi.id}
              secondaryAction={
                <Box>
                  <IconButton edge="end" onClick={() => handleEditPOI(poi)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => handleDeletePOI(poi.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText primary={poi.name} secondary={poi.type} />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Map */}
      <Box sx={{ height: 600, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        {bounds && (
          <MapContainer
            center={mapCenter}
            zoom={18}
            style={{ width: '100%', height: '100%' }}
            zoomControl={true}
          >
            {fullImageUrl && (
              <ImageOverlay url={fullImageUrl} bounds={bounds} opacity={1} zIndex={1} />
            )}

            <POIPlacementHandler enabled={placementMode} onPlace={handlePlacePOI} />

            {/* Render existing POIs */}
            {pois.map((poi) => (
              <POIMarker key={poi.id} poi={poi} onClick={handleEditPOI} floorPlan={floorPlan} />
            ))}
          </MapContainer>
        )}
      </Box>

      {/* POI Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPoi ? 'Edit POI' : 'Create New POI'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={poiForm.name}
            onChange={(e) => setPoiForm({ ...poiForm, name: e.target.value })}
            fullWidth
            margin="normal"
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              value={poiForm.poi_type_id}
              onChange={(e) => setPoiForm({ ...poiForm, poi_type_id: e.target.value })}
              label="Type"
            >
              {poiTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Description"
            value={poiForm.description}
            onChange={(e) => setPoiForm({ ...poiForm, description: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />

          <TextField
            label="Latitude"
            value={poiForm.geo_lat}
            onChange={(e) => setPoiForm({ ...poiForm, geo_lat: Number(e.target.value) })}
            fullWidth
            margin="normal"
            type="number"
            inputProps={{ step: '0.000001' }}
            disabled
          />

          <TextField
            label="Longitude"
            value={poiForm.geo_lng}
            onChange={(e) => setPoiForm({ ...poiForm, geo_lng: Number(e.target.value) })}
            fullWidth
            margin="normal"
            type="number"
            inputProps={{ step: '0.000001' }}
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSavePOI} variant="contained" disabled={!poiForm.name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default POIEditor;
